import { FC, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  HStack,
  Heading,
  Input,
  VStack,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';

import {
  useCreateNewPartnerMutation,
  displayNotification,
} from '../../../services';
import {
  isMutationSuccess,
  notifyMutationFailure,
} from '../../../utils/mutation-feedback';

export const SavedPartners: FC = () => {
  const navigate = useNavigate();

  const [createTeamMember] = useCreateNewPartnerMutation();

  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting },
    resetField,
    control,
    reset,
  } = useForm<{ id?: number; name: string }>();

  const navigateToPreviousPage = () => navigate(`/companies`);

  const [
    createPartner,
    { isLoading: isCreatePartnerLoading, status: createPartnerStatus },
  ] = useCreateNewPartnerMutation();

  const createNewPartner = async (data: { label: string; value: string }) => {
    const response = await createPartner({ name: data.value });

    if (!isMutationSuccess(response)) {
      notifyMutationFailure(response, {
        title: 'Could not create the company',
        description: 'The company was not created. Please try again.',
      });
    }
  };

  async function onSubmit(data: { name: string }) {
    const { name } = data;

    try {
      // Not unwrapped: a rejected create resolved normally and the page
      // navigated back to the list as if the company had been saved.
      const response = await createTeamMember({ name });

      if (!isMutationSuccess(response)) {
        notifyMutationFailure(response, {
          title: 'Could not create the company',
          description:
            'The company was not created. Please review the name and try again.',
        });
        return;
      }

      navigateToPreviousPage();
    } catch (error) {
      console.error(error);
      displayNotification({
        title: 'Something went wrong!',
        description:
          'The company was not created. Please try this action later or contact BrickLynk support.',
        status: 'error',
      });
    }
  }

  return (
    <Box>
      <HStack justify="space-between" mt="4" align="start">
        <Heading color="custom_blue.1" fontSize="2xl">
          {'Add new company'}
        </Heading>
      </HStack>

      <VStack
        as="form"
        align="stretch"
        gap="5"
        onSubmit={handleSubmit(onSubmit)}
        bg="white"
        border="1px"
        borderColor="custom_gray.3"
        mt="7"
        mb="4"
        p="5"
        borderRadius="md"
        minH="80vh"
        h="auto"
      >
        <HStack gap="4" align="flex-start">
          <VStack color="custom_blue.1" align="start" flexBasis="25%">
            <Box fontSize="sm" fontWeight="semibold">
              Company Name
            </Box>
          </VStack>
          <FormControl isInvalid={!!errors.name} flexBasis="50%" mt="2">
            <Input
              placeholder="Company Name"
              {...register('name', {
                required: 'Company Name is required',
              })}
            />
            <FormErrorMessage>
              {errors.name && errors.name.message}
            </FormErrorMessage>
          </FormControl>
        </HStack>

        <HStack mt={4} justify="end">
          <Button onClick={navigateToPreviousPage}>Cancel</Button>
          <Button
            isLoading={isCreatePartnerLoading || isSubmitting}
            type="submit"
            bg="custom_orange.2"
            _hover={{ bg: 'custom_orange.2' }}
            color="white"
          >
            Save
          </Button>
        </HStack>
      </VStack>
    </Box>
  );
};
